import "lib/polyfills";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import "./index.css";
import "views/style.css";
import "views/colors.css";
import "views/globalVars.css";
import registerServiceWorker from "./registerServiceWorker";
import history from "./state/history";
import store from "./state/store";
import Routes from "./routes";
import { createServiceWorkerNavigationListener } from "lib/serviceWorkerReloadHandler";
import { default as UpgradeModal } from "views/containers/UpgradeModalContainer";
import { default as ConfirmationModal } from "views/containers/ConfirmationModalContainer";
import { default as CollectionPreviewModal } from "views/containers/CollectionPreviewModalContainer";
import { default as RequestApprovalModal } from "views/containers/RequestApprovalModalContainer";
import { default as ApprovalModal } from "views/components/ApprovalModal/ApprovalModalContainer";
import { default as PurchaseCollectionModal } from "views/components/PurchaseCollection/PurchaseCollectionModalContainerGlobal.js";
import { default as DndUploadProgressToast } from "views/components/DndUploadProgressToast";
import {
  sessionRecorder,
  appsignal,
  initializeGlobalErrorHandlers,
  createTestErrorHelper
} from "lib";
import { ToastContainer, Slide } from "react-toastify";
import { ErrorBoundary } from "@appsignal/react";
import Copymaker from "lib/Copymaker";
import InputMode from "lib/InputMode";
import Render from "lib/Render";
import SSO from "lib/SSO";
import { default as ConfirmationModalLib } from "lib/ConfirmationModal/ConfirmationModal";
import { registerRenderCssAnimation } from "lib/renderCssAnimation";
import { registerRenderCaption } from "lib/renderCaption";
import { registerInjectDesignData } from "lib/injectDesignData";

// Register window APIs for server-side rendering (Puppeteer)
if (Render.isRenderMode()) {
  registerRenderCssAnimation();
  registerRenderCaption();
  registerInjectDesignData();
} else {
  SSO.init();

  ConfirmationModalLib.config({ store });

  // SessionRewind only runs in production (handled internally by sessionRecorder.init)
  sessionRecorder.init();

  // Initialize global error handlers for AppSignal
  // Redux context is captured by the middleware and accessed by error handlers
  initializeGlobalErrorHandlers(appsignal);

  // Create test error helper for browser console testing
  createTestErrorHelper(appsignal);

  Copymaker.startModeIfNeeded();
  InputMode.startModeIfNeeded();
}

const Modals = () => {
  return (
    <React.Fragment>
      <UpgradeModal />
      <ConfirmationModal />
      <CollectionPreviewModal />
      <RequestApprovalModal />
      <ApprovalModal />
      <PurchaseCollectionModal />
    </React.Fragment>
  );
};

const Root = () => {
  return (
    <span className="notranslate">
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <React.Fragment>
            <Routes />
            <Modals />
            <DndUploadProgressToast />
            <ToastContainer
              autoClose={8000}
              draggable={false}
              closeOnClick={false}
              position="bottom-right"
              hideProgressBar
              style={{
                width: "450px",
                bottom: "0.25em",
                right: "2em"
              }}
              closeButton={false}
              transition={Slide}
            />
          </React.Fragment>
        </ConnectedRouter>
      </Provider>
    </span>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorBoundary instance={appsignal} action="Easil Web UI">
    <Root />
  </ErrorBoundary>
);

registerServiceWorker();

// Add event listeners on location change. This ensures that SPA navigation triggers a service
// worker update check like a browser navigation event would.
history.listen(
  createServiceWorkerNavigationListener({
    initialLocation: history.location,
    store,
    serviceWorker: navigator.serviceWorker,
    window
  })
);
